/** Pure approval-decision validation shared by the UI and the mock daemon. */
import type { ApprovalDecision, ErrorCode, RiskLevel } from '@luminis/protocol';
import type { ApprovalView } from './types';
import { nowSeconds } from './id';

export type DecideValidation = { ok: true } | { ok: false; code: ErrorCode; message: string };

export const HIGH_RISK_LEVELS: readonly RiskLevel[] = ['destructive'];

export function isHighRisk(risk: RiskLevel): boolean {
  return HIGH_RISK_LEVELS.includes(risk);
}

export function isExpired(approval: Pick<ApprovalView, 'expires_at'>, now = nowSeconds()): boolean {
  return approval.expires_at <= now;
}

export interface DecideInput {
  decision: ApprovalDecision;
  args_hash: string;
  confirm_high_risk?: boolean;
  now?: number;
}

/**
 * Enforces the approval rules that live on the phone side:
 * - only pending, unexpired approvals can be decided;
 * - the args hash must match what was shown (the signature binds it);
 * - destructive actions need an explicit second confirmation;
 * - `allow_always` is not offered for destructive actions (each high-risk
 *   action must be approved individually, docs/DECISIONS.md A5/A6).
 */
export function validateDecision(
  approval: Pick<ApprovalView, 'status' | 'risk' | 'expires_at' | 'args_hash'>,
  input: DecideInput,
): DecideValidation {
  const now = input.now ?? nowSeconds();
  if (approval.status !== 'pending') {
    return { ok: false, code: 'CONFLICT', message: '该审批已被处理' };
  }
  if (approval.expires_at <= now) {
    return { ok: false, code: 'APPROVAL_EXPIRED', message: '审批已过期（默认 24h）' };
  }
  if (input.args_hash !== approval.args_hash) {
    return { ok: false, code: 'CONFLICT', message: '参数哈希不匹配，拒绝操作' };
  }
  if (input.decision === 'deny') return { ok: true };
  if (isHighRisk(approval.risk)) {
    if (input.decision === 'allow_always') {
      return {
        ok: false,
        code: 'RISK_BLOCKED',
        message: '高危操作不允许永久白名单，请选择一次性/本任务/限时',
      };
    }
    if (!input.confirm_high_risk) {
      return { ok: false, code: 'APPROVAL_REQUIRED', message: '高危操作需要二次确认' };
    }
  }
  return { ok: true };
}

export const DECISION_LABELS: Record<ApprovalDecision, string> = {
  allow_once: '允许一次',
  allow_task: '本任务',
  allow_15m: '限时 15 分钟',
  allow_always: '永久白名单',
  deny: '拒绝',
};

/** Pending filter used by the approval centre (expired pending items count as done). */
export type ApprovalFilter = 'pending' | 'done' | 'all';

export const APPROVAL_FILTER_LABELS: Record<ApprovalFilter, string> = {
  pending: '待处理',
  done: '已处理',
  all: '全部',
};

/** Still actionable: pending and not past the 24h expiry. */
export function isApprovalOpen(
  approval: Pick<ApprovalView, 'status' | 'expires_at'>,
  now = nowSeconds(),
): boolean {
  return approval.status === 'pending' && approval.expires_at > now;
}

export function filterApprovals(
  approvals: readonly ApprovalView[],
  filter: ApprovalFilter,
  now = nowSeconds(),
): ApprovalView[] {
  if (filter === 'all') return [...approvals];
  if (filter === 'pending') return approvals.filter((approval) => isApprovalOpen(approval, now));
  return approvals.filter((approval) => !isApprovalOpen(approval, now));
}

export function approvalCounts(
  approvals: readonly ApprovalView[],
  now = nowSeconds(),
): { pending: number; done: number; all: number } {
  const pending = approvals.filter((approval) => isApprovalOpen(approval, now)).length;
  return { pending, done: approvals.length - pending, all: approvals.length };
}

/** How long the user has been waiting (seconds). */
export function approvalWaitSeconds(
  approval: Pick<ApprovalView, 'requested_at'>,
  now = nowSeconds(),
): number {
  return Math.max(0, Math.round(now - approval.requested_at));
}

/**
 * Remaining share (0–1) of the approval's validity window, for the expiry bar.
 * Computed from requested_at → expires_at so non-24h windows also render sanely.
 */
export function approvalExpiryRatio(
  approval: Pick<ApprovalView, 'requested_at' | 'expires_at'>,
  now = nowSeconds(),
): number {
  const total = approval.expires_at - approval.requested_at;
  if (!(total > 0)) return 0;
  return Math.min(1, Math.max(0, (approval.expires_at - now) / total));
}

export const DECISION_HINTS: Record<ApprovalDecision, string> = {
  allow_once: '仅执行这一次，最小授权',
  allow_task: '同一任务内后续同类步骤自动通过',
  allow_15m: '15 分钟内同类操作自动通过',
  allow_always: '加入永久白名单（高危不可用）',
  deny: '拒绝并记录审计',
};

export const RISK_LABELS: Record<RiskLevel, string> = {
  read: '只读',
  write: '写入',
  external: '外部',
  destructive: '高危',
};

export const RISK_DESCRIPTIONS: Record<RiskLevel, string> = {
  read: '只读取信息，不改变系统状态',
  write: '会写入文件或修改配置',
  external: '会访问外部网络/服务',
  destructive: '可能删除数据、改系统设置或不可恢复',
};

export const RISK_TONES: Record<RiskLevel, string> = {
  read: 'text-emerald-300 border-emerald-400/40 bg-emerald-400/10',
  write: 'text-amber-200 border-amber-400/40 bg-amber-400/10',
  external: 'text-sky-200 border-sky-400/40 bg-sky-400/10',
  destructive: 'text-rose-200 border-rose-400/50 bg-rose-500/15',
};

export function decisionScopeSummary(decision: ApprovalDecision): string {
  switch (decision) {
    case 'allow_once':
      return '一次性授权';
    case 'allow_task':
      return '本任务授权';
    case 'allow_15m':
      return '限时 15 分钟授权';
    case 'allow_always':
      return '永久白名单授权';
    case 'deny':
      return '已拒绝';
  }
}
